import React from "react";
import type { FieldDefinition, PartnerData, ReelScene, SceneInputs, SceneMeta, WatermarkConfig, WatermarkPosition } from "../../api";
import { api } from "../../api";
import { MediaPicker } from "./MediaPicker";
import {
  hasSceneWatermarkOverride,
  isSceneWatermarkVisible,
  resolveSceneWatermark,
  SCENE_WM_KEYS,
} from "../../lib/sceneWatermark";
import { hasDedicatedSceneRecipe } from "../../lib/sceneSfxRecipes";

type Props = {
  scene: ReelScene | null;
  meta: SceneMeta | null;
  onChange: (next: ReelScene) => void;
  /** Globale Watermark-Konfiguration — entscheidet, ob der per-Scene
   *  „Logo in dieser Scene"-Toggle überhaupt wirken kann. Wenn das globale
   *  Watermark im Logo-Tab ausgeschaltet ist, ist auch der Scene-Toggle
   *  wirkungslos und wird disabled angezeigt. */
  watermark?: WatermarkConfig;
  /** Anzahl der Auto-SFX-Beats, die aktuell für diese Scene gesetzt sind
   *  (gezählt per `sfxEvent.sceneUid === scene.uid`). 0 = Auto-SFX aus,
   *  >0 = Auto-SFX an (Button zeigt „Erneut setzen" + „Entfernen"). */
  autoSfxBeatsCount?: number;
  /** Setzt das passende SFX-Rezept dieser Scene. Idempotent (Toggle-Logik). */
  onApplyAutoSfx?: (sceneUid: string) => void;
  /** Entfernt alle Auto-SFX-Beats, die zu dieser Scene gehören. */
  onClearAutoSfx?: (sceneUid: string) => void;
};

const TRANSITIONS = [
  { id: "none", label: "kein Übergang" },
  { id: "crossfade", label: "Crossfade" },
  { id: "wipeLeft", label: "Wipe ←" },
  { id: "wipeRight", label: "Wipe →" },
  { id: "slideUp", label: "Slide ↑" },
  { id: "slideDown", label: "Slide ↓" },
  { id: "slideLeft", label: "Slide ←" },
  { id: "slideRight", label: "Slide →" },
  { id: "zoomIn", label: "Zoom rein" },
  { id: "zoomOut", label: "Zoom raus" },
  { id: "blurDissolve", label: "Blur-Auflösung" },
  { id: "glideUp", label: "Glide (sanft ↑)" },
  { id: "flash", label: "Flash" },
] as const;

export const Inspector: React.FC<Props> = ({
  scene,
  meta,
  onChange,
  watermark,
  autoSfxBeatsCount = 0,
  onApplyAutoSfx,
  onClearAutoSfx,
}) => {
  if (!scene || !meta) {
    return (
      <div className="inspector empty">
        <p>Wähle einen Clip auf der Timeline aus, um Texte, Variante und Übergang zu bearbeiten.</p>
      </div>
    );
  }

  /**
   * If the meta declares an `autoDuration` recipe AND the user hasn't opted
   * out (`extras.autoDuration === false`), recompute the natural animation
   * duration from the source-input length. Currently only `items` is wired
   * up. Returns `null` when auto-duration shouldn't fire (no recipe, opted
   * out, empty source) so the caller can keep the existing values intact.
   */
  const computeAutoNatural = (inputs: SceneInputs): number | null => {
    const cfg = meta.autoDuration;
    if (!cfg) return null;
    if (inputs.extras?.autoDuration === false) return null;
    const count =
      cfg.source === "items" ? (Array.isArray(inputs.items) ? inputs.items.length : 0) : 0;
    if (count <= 0) return null;
    const raw = count * cfg.perItem + (cfg.padding ?? 0);
    const clamped = Math.min(
      cfg.maxSec ?? 60,
      Math.max(cfg.minSec ?? 0.4, raw),
    );
    return Math.round(clamped * 10) / 10;
  };

  /**
   * Apply the auto-duration recipe and SCALE it by the current playback rate.
   * The Timeline-Fenster (durationSec) schrumpft bei rate>1 entsprechend,
   * damit der Speed-Effekt sichtbar wird; die authored Animationsdauer wird
   * als `naturalDurationSec` festgehalten, sobald rate ≠ 1.
   */
  const applyAutoDuration = (
    nextScene: ReelScene,
    inputs: SceneInputs,
  ): ReelScene => {
    const natural = computeAutoNatural(inputs);
    if (natural === null) return nextScene;
    const rate = nextScene.playbackRate ?? 1;
    const isOne = Math.abs(rate - 1) < 0.001;
    const newDuration = Math.max(0.4, Math.round((natural / rate) * 10) / 10);
    return {
      ...nextScene,
      durationSec: newDuration,
      naturalDurationSec: isOne ? nextScene.naturalDurationSec : natural,
    };
  };

  const setInput = <K extends keyof SceneInputs>(key: K, val: SceneInputs[K]): void => {
    const inputs = { ...scene.inputs, [key]: val };
    const next = applyAutoDuration({ ...scene, inputs }, inputs);
    onChange(next);
  };

  const setExtra = (key: string, val: string | number | boolean): void => {
    const extras = { ...(scene.inputs.extras ?? {}), [key]: val };
    const inputs = { ...scene.inputs, extras };
    const next = applyAutoDuration({ ...scene, inputs }, inputs);
    onChange(next);
  };

  const sfxOn = (scene.inputs.extras?.sfx as boolean | undefined) !== false;
  const musicOn = (scene.inputs.extras?.muteMusic as boolean | undefined) !== true;
  const sceneTheme =
    scene.inputs.extras?.theme === "light"
      ? "light"
      : scene.inputs.extras?.theme === "dark"
        ? "dark"
        : undefined;
  const setSceneTheme = (val: "dark" | "light" | undefined): void => {
    const extras: Record<string, string | number | boolean | unknown[] | Record<string, unknown> | undefined> = {
      ...(scene.inputs.extras ?? {}),
    };
    if (val === undefined) delete extras.theme;
    else extras.theme = val;
    const inputs = { ...scene.inputs, extras };
    onChange(applyAutoDuration({ ...scene, inputs }, inputs));
  };
  // Effective visibility = same logic the engine uses to decide whether to
  // actually render the per-clip watermark. For "logo scenes" (signOff &
  // friends) the default is OFF, so the toggle starts in „Verborgen" until
  // the user explicitly enables it.
  const logoVisible = isSceneWatermarkVisible(
    scene.sceneId,
    scene.inputs.extras,
    !!watermark?.enabled,
  );
  const offsetX = Number(scene.inputs.extras?.offsetX ?? 0);
  const offsetY = Number(scene.inputs.extras?.offsetY ?? 0);
  const scale = Number(scene.inputs.extras?.scale ?? 1);
  const autoDurationSupported = !!meta.autoDuration;
  const autoDurationOn =
    autoDurationSupported && (scene.inputs.extras?.autoDuration as boolean | undefined) !== false;

  return (
    <div className="inspector">
      <div className="inspector-head">
        <div className="inspector-title">{meta.label}</div>
        <div className="inspector-sub">{meta.description}</div>
      </div>

      <label className="form-row">
        <span>
          Dauer (s)
          {autoDurationOn ? <small style={{ marginLeft: 6, opacity: 0.6 }}>· auto</small> : null}
        </span>
        <input
          type="number"
          min={0.4}
          max={60}
          step={0.1}
          value={scene.durationSec}
          disabled={autoDurationOn}
          title={
            autoDurationOn
              ? "Dauer wird automatisch aus der Item-Anzahl berechnet — Toggle „Dauer automatisch“ ausschalten, um manuell zu setzen."
              : "Speed-Mode: ändert die Dauer und passt automatisch die Clip-Geschwindigkeit an, damit die Animation das neue Fenster ausfüllt — kein Standbild am Ende."
          }
          onChange={(e) => {
            const raw = parseFloat(e.target.value || "0");
            const newDuration = Math.max(0.4, Math.round(raw * 10) / 10);
            // Speed-Mode wie beim Drag-Resize auf der Timeline: die authored
            // Animations-Länge (`naturalDurationSec`) bleibt verankert,
            // `playbackRate` wird so gesetzt, dass die Animation das neue
            // Timeline-Fenster exakt ausfüllt — Streck = langsamer, Stauch =
            // schneller, kein eingefrorenes Standbild + Outro-Fade am Ende.
            const natural = scene.naturalDurationSec ?? scene.durationSec;
            const safeNatural = Math.max(0.4, natural);
            const rawRate = safeNatural / newDuration;
            const rate = Math.min(4, Math.max(0.25, Math.round(rawRate * 100) / 100));
            const isOne = Math.abs(rate - 1) < 0.005;
            onChange({
              ...scene,
              durationSec: newDuration,
              playbackRate: isOne ? undefined : rate,
              naturalDurationSec: isOne ? undefined : Math.round(safeNatural * 10) / 10,
            });
          }}
        />
      </label>

      <label className="form-row">
        <span title="Nur diese Vorlagen-Scene — dieselbe Logik in Vorschau und Export. Transport-Leiste (0,5×/1×/2×) ist nur die Player-Vorschau, nicht das MP4.">
          Clip-Geschwindigkeit ({(scene.playbackRate ?? 1).toFixed(2)}×)
          {scene.naturalDurationSec ? (
            <small style={{ marginLeft: 6, opacity: 0.6 }}>
              · {scene.naturalDurationSec.toFixed(1)}s authored
            </small>
          ) : null}
        </span>
        <input
          type="range"
          min={0.25}
          max={4}
          step={0.05}
          value={scene.playbackRate ?? 1}
          onChange={(e) => {
            const v = parseFloat(e.target.value || "1");
            const clamped = Math.min(4, Math.max(0.25, v));
            const rate = Math.round(clamped * 100) / 100;
            const isOne = Math.abs(rate - 1) < 0.001;
            // Anker = die authored Animations-Dauer. Wenn sie noch nicht
            // explizit gesetzt ist (rate war bisher 1), nehmen wir den
            // aktuellen Timeline-Slot als Anker. Damit bleibt die Animation
            // an ihrer natürlichen Geschwindigkeit verankert und das
            // Timeline-Fenster schrumpft/wächst proportional zur Speed.
            const natural = scene.naturalDurationSec ?? scene.durationSec;
            const newDuration = Math.max(0.4, Math.round((natural / rate) * 10) / 10);
            onChange({
              ...scene,
              playbackRate: isOne ? undefined : rate,
              // Bei rate=1 (zurück auf normal) lösen wir den Anker, damit
              // spätere Item-/Inputs-Änderungen wieder die normale
              // Auto-Dauer treffen.
              naturalDurationSec: isOne ? undefined : Math.round(natural * 10) / 10,
              durationSec: newDuration,
            });
          }}
        />
      </label>

      <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
        <span title="Theme dieser einzelnen Szene. „Auto“ folgt dem Reel-Theme (Toolbar-Schalter 🌙/☀️). Dark/Light überschreibt es NUR für diese Szene — so kannst du gemischte Reels bauen.">
          🎨 Theme dieser Scene
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          {([
            { v: undefined, label: "Auto" },
            { v: "dark" as const, label: "🌙 Dark" },
            { v: "light" as const, label: "☀️ Light" },
          ]).map((opt) => {
            const active = sceneTheme === opt.v || (opt.v === undefined && sceneTheme === undefined);
            return (
              <button
                key={opt.label}
                type="button"
                className="btn ghost sm"
                onClick={() => setSceneTheme(opt.v)}
                aria-pressed={active}
                style={{
                  flex: 1,
                  padding: "7px 8px",
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 8,
                  cursor: "pointer",
                  background: active ? "rgb(34, 211, 197)" : "rgba(255,255,255,0.04)",
                  color: active ? "#0a0e14" : "rgba(255,255,255,0.75)",
                  border: `1px solid ${active ? "rgb(34, 211, 197)" : "rgba(255,255,255,0.16)"}`,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {autoDurationSupported ? (
        <label className="form-row toggle-row">
          <span title="Automatisch nachjustieren, wenn du Items hinzufügst/entfernst.">
            ⏱ Dauer automatisch
          </span>
          <button
            type="button"
            className={`toggle ${autoDurationOn ? "on" : "off"}`}
            onClick={() => setExtra("autoDuration", !autoDurationOn)}
            aria-pressed={autoDurationOn}
          >
            <span className="dot" />
            <span className="lbl">{autoDurationOn ? "An" : "Manuell"}</span>
          </button>
        </label>
      ) : null}

      <label className="form-row toggle-row">
        <span title="Mutet die eingebauten Vorlagen-SFX dieser Scene. Eigene SFX-Events, die du auf die Timeline gezogen hast, sind davon NICHT betroffen — die spielst du weiter ab oder löscht sie einzeln.">
          🔊 Vorlagen-SFX dieser Scene
        </span>
        <button
          type="button"
          className={`toggle ${sfxOn ? "on" : "off"}`}
          onClick={() => setExtra("sfx", !sfxOn)}
          aria-pressed={sfxOn}
        >
          <span className="dot" />
          <span className="lbl">{sfxOn ? "An" : "Stumm"}</span>
        </button>
      </label>

      {/* Auto-SFX-Block: Ein-Klick-Sound-Set passend zur Choreographie der
          Scene. Beats werden als reguläre `sfxEvents` mit `sceneUid`-Tag
          gesetzt — frei verschiebbar, einzeln stummbar, jederzeit wieder
          komplett entfernbar.  */}
      <div
        className="form-row"
        style={{
          flexDirection: "column",
          alignItems: "stretch",
          gap: 8,
          padding: "10px 12px",
          background: "rgba(34, 211, 197, 0.06)",
          border: "1px solid rgba(34, 211, 197, 0.25)",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            title={
              hasDedicatedSceneRecipe(scene.sceneId)
                ? "Setzt automatisch passende Sound-Effekte exakt auf die Animations-Beats dieser Scene (Wischer, Reveals, Drops). Beats erscheinen als ganz normale SFX-Bars auf der Timeline und können einzeln verschoben, gelöscht oder mit anderen Sounds getauscht werden."
                : "Diese Scene hat noch kein dediziertes Rezept — du bekommst einen sinnvollen generischen Sound-Mix (Intro-Swoosh, Item-Taps, Outro-Transition)."
            }
            style={{ fontWeight: 600, fontSize: 13 }}
          >
            ✨ Auto-SFX für diese Scene
          </span>
          {autoSfxBeatsCount > 0 ? (
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(34, 211, 197, 0.18)",
                color: "rgb(34, 211, 197)",
                fontWeight: 600,
              }}
            >
              {autoSfxBeatsCount} aktiv
            </span>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn"
            disabled={!onApplyAutoSfx}
            onClick={() => onApplyAutoSfx?.(scene.uid)}
            title={
              autoSfxBeatsCount > 0
                ? "Aktuelle Auto-SFX-Beats verwerfen und mit neuer Choreographie neu setzen (z. B. nach geänderter Item-Anzahl oder Dauer)."
                : "Passendes Sound-Set für diese Scene auf die Timeline setzen."
            }
            style={{
              flex: 1,
              minWidth: 140,
              padding: "8px 10px",
              background:
                autoSfxBeatsCount > 0
                  ? "rgba(34, 211, 197, 0.12)"
                  : "rgb(34, 211, 197)",
              color: autoSfxBeatsCount > 0 ? "rgb(34, 211, 197)" : "#0a0e14",
              border:
                autoSfxBeatsCount > 0
                  ? "1px solid rgba(34, 211, 197, 0.4)"
                  : "1px solid rgb(34, 211, 197)",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {autoSfxBeatsCount > 0
              ? "↻ Auto-SFX neu setzen"
              : "✨ Passende SFX einsetzen"}
          </button>
          {autoSfxBeatsCount > 0 ? (
            <button
              type="button"
              className="btn"
              disabled={!onClearAutoSfx}
              onClick={() => onClearAutoSfx?.(scene.uid)}
              title="Entfernt nur die Auto-SFX-Beats dieser Scene. Manuell hinzugefügte SFX bleiben unangetastet."
              style={{
                padding: "8px 12px",
                background: "transparent",
                color: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              🗑 Entfernen
            </button>
          ) : null}
        </div>

        <div
          style={{
            fontSize: 11,
            opacity: 0.7,
            lineHeight: 1.4,
          }}
        >
          {hasDedicatedSceneRecipe(scene.sceneId) ? (
            <>Beats sind auf die Animation dieser Scene abgestimmt (Wischer, Reveals, Drops). Werden als reguläre SFX-Bars auf der Timeline platziert — frei verschiebbar, einzeln löschbar, mit dem reel-weiten SFX-Master regelbar.</>
          ) : (
            <>Generisches Sound-Set wird gesetzt (Intro-Swoosh, Item-Taps, Outro). Du kannst einzelne Beats anschließend wie gewohnt auf der Timeline anpassen.</>
          )}
        </div>
      </div>

      <label className="form-row toggle-row">
        <span title="Zieht das Musik-Volumen während dieser Scene auf 0 — andere Scenes spielen Musik weiter ab.">
          🎵 Musik in dieser Scene
        </span>
        <button
          type="button"
          className={`toggle ${musicOn ? "on" : "off"}`}
          onClick={() => setExtra("muteMusic", musicOn)}
          aria-pressed={musicOn}
        >
          <span className="dot" />
          <span className="lbl">{musicOn ? "An" : "Stumm"}</span>
        </button>
      </label>

      {watermark ? (
        <SceneWatermarkSection
          scene={scene}
          watermark={watermark}
          logoVisible={logoVisible}
          onToggleVisible={() => setExtra("hideLogo", logoVisible)}
          onPatchExtras={(patch) => {
            const extras: Record<string, string | number | boolean> = {
              ...(scene.inputs.extras ?? {}),
            };
            for (const [k, v] of Object.entries(patch)) {
              if (v === undefined) delete extras[k];
              else extras[k] = v;
            }
            const inputs = { ...scene.inputs, extras };
            onChange(applyAutoDuration({ ...scene, inputs }, inputs));
          }}
        />
      ) : null}

      {scene.sceneId === "partnerServiceDetail" ? (
        <ProductLinkLoader
          scene={scene}
          onApply={(patch) => {
            const extrasPatch = patch.extras ?? {};
            const extras = { ...(scene.inputs.extras ?? {}), ...extrasPatch };
            const inputs: SceneInputs = { ...scene.inputs, extras };
            if (patch.text !== undefined) inputs.text = patch.text;
            if (patch.subtext !== undefined) inputs.subtext = patch.subtext;
            if (patch.url !== undefined) inputs.url = patch.url;
            if (patch.media !== undefined) inputs.media = patch.media;
            onChange(applyAutoDuration({ ...scene, inputs }, inputs));
          }}
        />
      ) : null}

      {PARTNER_SCENES_WITH_LOADER.has(scene.sceneId) ? (
        <PartnerLinkLoader
          scene={scene}
          onApply={(patch) => {
            const extrasPatch = patch.extras ?? {};
            const extras = { ...(scene.inputs.extras ?? {}), ...extrasPatch };
            const inputs: SceneInputs = { ...scene.inputs, extras };
            if (patch.text !== undefined) inputs.text = patch.text;
            if (patch.subtext !== undefined) inputs.subtext = patch.subtext;
            if (patch.number !== undefined) inputs.number = patch.number;
            if (patch.unit !== undefined) inputs.unit = patch.unit;
            if (patch.url !== undefined) inputs.url = patch.url;
            if (patch.media !== undefined) inputs.media = patch.media;
            if (patch.items !== undefined) inputs.items = patch.items;
            onChange(applyAutoDuration({ ...scene, inputs }, inputs));
          }}
        />
      ) : null}

      {meta.fields.map((f) => {
        const isExtra = f.key.startsWith("extras.") || !KNOWN_KEYS.has(f.key);
        const subKey = f.key.startsWith("extras.") ? f.key.slice("extras.".length) : f.key;
        return (
          <FieldEditor
            key={f.key}
            field={f}
            value={isExtra ? scene.inputs.extras?.[subKey] : readField(scene.inputs, f.key)}
            variants={meta.variants}
            onChange={(v) => {
              if (isExtra) {
                setExtra(subKey, v as string | number | boolean);
              } else {
                setInput(f.key as keyof SceneInputs, v as never);
              }
            }}
          />
        );
      })}

      {scene.sceneId === "colorBlockReveal" ? (
        <AllyverSlotsEditor
          scene={scene}
          onPatchExtras={(patch) => {
            const extras = { ...(scene.inputs.extras ?? {}), ...patch };
            const inputs = { ...scene.inputs, extras };
            onChange(applyAutoDuration({ ...scene, inputs }, inputs));
          }}
        />
      ) : null}

      <details className="inspector-section">
        <summary>📐 Position & Scale</summary>
        <label className="form-row">
          <span>Offset X ({offsetX}px)</span>
          <input
            type="range"
            min={-300}
            max={300}
            step={2}
            value={offsetX}
            onChange={(e) => setExtra("offsetX", Number(e.target.value))}
          />
        </label>
        <label className="form-row">
          <span>Offset Y ({offsetY}px)</span>
          <input
            type="range"
            min={-400}
            max={400}
            step={2}
            value={offsetY}
            onChange={(e) => setExtra("offsetY", Number(e.target.value))}
          />
        </label>
        <label className="form-row">
          <span>Skalierung ({scale.toFixed(2)}×)</span>
          <input
            type="range"
            min={0.6}
            max={1.4}
            step={0.02}
            value={scale}
            onChange={(e) => setExtra("scale", Number(e.target.value))}
          />
        </label>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => {
            setExtra("offsetX", 0);
            setExtra("offsetY", 0);
            setExtra("scale", 1);
          }}
        >
          Zurücksetzen
        </button>
      </details>

      <label className="form-row">
        <span>Übergang zur nächsten Scene</span>
        <select
          value={scene.transition?.kind ?? "crossfade"}
          onChange={(e) =>
            onChange({
              ...scene,
              transition: {
                kind: e.target.value as ReelScene["transition"] extends infer T
                  ? T extends { kind: infer K }
                    ? K
                    : never
                  : never,
                durationFrames: scene.transition?.durationFrames ?? 8,
              },
            })
          }
        >
          {TRANSITIONS.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label className="form-row">
        <span>Übergangsdauer (Frames)</span>
        <input
          type="number"
          min={2}
          max={30}
          step={1}
          value={scene.transition?.durationFrames ?? 8}
          onChange={(e) =>
            onChange({
              ...scene,
              transition: {
                kind: scene.transition?.kind ?? "crossfade",
                durationFrames: parseInt(e.target.value || "8", 10),
              },
            })
          }
        />
      </label>
    </div>
  );
};

/**
 * Per-Scene Logo-Section im Inspector. Bündelt:
 *  - „Logo in dieser Scene" An/Aus (extras.hideLogo),
 *  - die geometrischen Overrides (Position, Größe, Opazität, Eck-Abstand),
 *  - „Globale Defaults verwenden"-Reset.
 *
 * Werte sind effective values (gemerged aus globalem Default + Override).
 * Der Patch-Callback schreibt ausschließlich nach `inputs.extras.wm*`.
 */
type ExtrasPatch = Record<string, string | number | boolean | undefined>;
const SceneWatermarkSection: React.FC<{
  scene: ReelScene;
  watermark: WatermarkConfig;
  logoVisible: boolean;
  onToggleVisible: () => void;
  onPatchExtras: (patch: ExtrasPatch) => void;
}> = ({ scene, watermark, logoVisible, onToggleVisible, onPatchExtras }) => {
  const extras = scene.inputs.extras ?? {};
  const eff = resolveSceneWatermark(watermark, extras);
  const hasOverride = hasSceneWatermarkOverride(extras);
  const isFreePlaced = typeof eff.xPct === "number" && typeof eff.yPct === "number";

  const setPos = (p: WatermarkPosition): void => {
    onPatchExtras({
      wmPosition: p,
      wmXPct: undefined,
      wmYPct: undefined,
      wmOffsetX: 0,
      wmOffsetY: 0,
    });
  };

  const resetAll = (): void => {
    const patch: ExtrasPatch = {};
    for (const k of SCENE_WM_KEYS) patch[k] = undefined;
    onPatchExtras(patch);
  };

  const disabled = !watermark.enabled;

  return (
    <details className="inspector-section" open>
      <summary>
        🎯 Logo in dieser Scene
        {hasOverride ? <span className="tab-badge" style={{ marginLeft: 6 }}>eigene</span> : null}
      </summary>

      <label className="form-row toggle-row">
        <span>
          Sichtbar
          {disabled ? (
            <small style={{ display: "block", color: "#94a3b8", fontWeight: 400, marginTop: 2 }}>
              Globales Logo ist im Logo-Tab ausgeschaltet
            </small>
          ) : null}
        </span>
        <button
          type="button"
          className={`toggle ${logoVisible ? "on" : "off"}`}
          onClick={onToggleVisible}
          aria-pressed={logoVisible}
          disabled={disabled}
        >
          <span className="dot" />
          <span className="lbl">{logoVisible ? "Sichtbar" : "Verborgen"}</span>
        </button>
      </label>

      <label className="form-row">
        <span>
          Position{" "}
          {isFreePlaced ? (
            <small style={{ color: "#94a3b8" }}>
              · frei platziert ({Math.round(eff.xPct ?? 0)}% / {Math.round(eff.yPct ?? 0)}%)
            </small>
          ) : null}
        </span>
        <div className="wm-pos-grid">
          {(["tl", "tr", "bl", "br"] as const).map((p) => (
            <button
              key={p}
              type="button"
              className={`wm-pos ${!isFreePlaced && eff.position === p ? "active" : ""}`}
              onClick={() => setPos(p)}
              disabled={disabled || !logoVisible}
              title={p.toUpperCase()}
            >
              <span className={`wm-dot ${p}`} />
            </button>
          ))}
        </div>
      </label>

      {isFreePlaced ? (
        <button
          type="button"
          className="btn ghost sm"
          disabled={disabled || !logoVisible}
          onClick={() => onPatchExtras({ wmXPct: undefined, wmYPct: undefined })}
          style={{ alignSelf: "flex-start" }}
        >
          ↺ Auf Ecke zurücksetzen
        </button>
      ) : null}

      <label className="form-row">
        <span>Größe ({eff.size}px)</span>
        <input
          type="range"
          min={24}
          max={400}
          value={eff.size}
          disabled={disabled || !logoVisible}
          onChange={(e) => onPatchExtras({ wmSize: Number(e.target.value) })}
        />
      </label>

      <label className="form-row">
        <span>Opazität ({Math.round(eff.opacity * 100)}%)</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(eff.opacity * 100)}
          disabled={disabled || !logoVisible}
          onChange={(e) => onPatchExtras({ wmOpacity: Number(e.target.value) / 100 })}
        />
      </label>

      {!isFreePlaced ? (
        <label className="form-row">
          <span>Eck-Abstand ({eff.paddingPx}px)</span>
          <input
            type="range"
            min={0}
            max={200}
            value={eff.paddingPx}
            disabled={disabled || !logoVisible}
            onChange={(e) => onPatchExtras({ wmPaddingPx: Number(e.target.value) })}
          />
        </label>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <small style={{ color: "#94a3b8" }}>
          {hasOverride ? "Diese Scene benutzt eigene Werte" : "Diese Scene folgt den globalen Defaults"}
        </small>
        {hasOverride ? (
          <button
            type="button"
            className="btn ghost sm"
            onClick={resetAll}
            title="Alle eigenen Logo-Werte dieser Scene löschen — fällt zurück auf die Defaults aus dem Logo-Tab"
          >
            ↺ Globale Defaults
          </button>
        ) : null}
      </div>
    </details>
  );
};

/**
 * URL-Loader für die "Partner Service-Detail"-Scene. Marketer paste an
 * Allyver-Produkt-Link, klicken "Aus Allyver-Link laden" und wir füllen
 * Bild, Preis, Titel, Verkäufer, Standort, Beschreibung und CTA-URL
 * automatisch über `/api/partner-product-extract`. Das Bild wird auf
 * `/public/uploads` gespiegelt, damit es im lokalen Renderer verfügbar
 * ist (analog zum bestehenden Partner-Modal).
 */
type ProductLoaderPatch = {
  text?: string;
  subtext?: string;
  url?: string;
  media?: string[];
  extras?: Record<string, string | number | boolean>;
};

const ProductLinkLoader: React.FC<{
  scene: ReelScene;
  onApply: (patch: ProductLoaderPatch) => void;
}> = ({ scene, onApply }) => {
  const initialUrl = String(scene.inputs.extras?.productUrl ?? "").trim();
  const [url, setUrl] = React.useState<string>(initialUrl);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [hint, setHint] = React.useState<string | null>(null);

  React.useEffect(() => {
    setUrl(String(scene.inputs.extras?.productUrl ?? "").trim());
    setErr(null);
    setHint(null);
  }, [scene.uid, scene.inputs.extras?.productUrl]);

  const load = async (): Promise<void> => {
    const trimmed = url.trim();
    if (!trimmed) {
      setErr("Bitte URL eingeben.");
      return;
    }
    setLoading(true);
    setErr(null);
    setHint(null);
    try {
      const data = await api.partnerProductExtract(trimmed, { mirror: true });
      const extras: Record<string, string | number | boolean> = {
        productUrl: data.sourceUrl,
      };
      if (data.priceLabel) extras.price = data.priceLabel;
      if (data.location) extras.location = data.location;
      if (data.description) extras.description = data.description;
      if (data.categoryTag) extras.kategorieType = data.categoryTag;
      onApply({
        text: data.name,
        subtext: data.sellerName ?? scene.inputs.subtext,
        url: data.displayUrl,
        media: data.imageUrl ? [data.imageUrl] : undefined,
        extras,
      });
      setHint(`Geladen: ${data.name}${data.priceLabel ? ` · ${data.priceLabel}` : ""}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="form-row"
      style={{
        background: "rgba(99, 102, 241, 0.08)",
        border: "1px solid rgba(99, 102, 241, 0.25)",
        borderRadius: 10,
        padding: 10,
        gap: 6,
      }}
    >
      <span style={{ fontWeight: 600 }}>
        🔗 Aus Allyver-Produkt-Link laden
        <small style={{ display: "block", fontWeight: 400, opacity: 0.7, marginTop: 2 }}>
          Bild, Preis, Beschreibung, Verkäufer & CTA werden automatisch befüllt.
        </small>
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="url"
          value={url}
          placeholder="https://allyver.com/product/seedos-all-in-one-taste-box-2"
          onChange={(e) => setUrl(e.target.value)}
          disabled={loading}
          style={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void load();
            }
          }}
        />
        <button
          type="button"
          className="btn primary"
          onClick={() => void load()}
          disabled={loading || !url.trim()}
        >
          {loading ? "…" : "Laden"}
        </button>
      </div>
      {err ? (
        <small style={{ color: "#f87171" }}>{err}</small>
      ) : hint ? (
        <small style={{ color: "#34d399" }}>{hint}</small>
      ) : null}
    </div>
  );
};

const KNOWN_KEYS = new Set<string>([
  "text",
  "subtext",
  "number",
  "unit",
  "items",
  "media",
  "url",
  "variant",
  "accent",
]);

/* =========================================================================
 *  Partner-Link-Loader: gemeinsamer URL-Eingang für alle Partner-Szenen
 *  (außer `partnerServiceDetail`, das einen eigenen Produkt-URL-Loader hat).
 *
 *  Workflow:
 *   1. User fügt eine Allyver-Händler-URL ein (z. B. https://allyver.com/jusatech).
 *   2. `api.partnerExtract(url, { mirror: true })` lädt die JSON-LD-/HTML-
 *      Daten des Partners und spiegelt das Logo + Produktbilder lokal nach
 *      `/public/uploads` (damit der Renderer ohne CORS rankommt).
 *   3. Pro Szene mappt eine spezifische Funktion die Partner-Felder auf die
 *      Scene-Inputs (z. B. `partnerCardFlip` braucht Name+Stadt+Logo+Kategorie,
 *      `partnerCategoryLineup` möchte einen neuen Eintrag in `items` anhängen).
 * ========================================================================= */

type PartnerLoaderPatch = {
  text?: string;
  subtext?: string;
  number?: string;
  unit?: string;
  url?: string;
  media?: string[];
  items?: string[];
  extras?: Record<string, string | number | boolean>;
};

type PartnerLoaderMode = "replace" | "append";

type PartnerSceneMapping = {
  /** Wie der Patch auf die Scene angewendet wird (überschreibt vs. hängt an `items` an). */
  mode: PartnerLoaderMode;
  /** Konvertiert `PartnerData` (vom Server) → patch (für `setInput` / `setExtra`). */
  build: (data: PartnerData, currentItems: string[]) => PartnerLoaderPatch;
  /** Optionaler Placeholder-Text im URL-Input. */
  placeholder?: string;
};

const PARTNER_SCENE_MAPPINGS: Record<string, PartnerSceneMapping> = {
  partnerCardFlip: {
    mode: "replace",
    build: (d) => ({
      text: d.name.toUpperCase(),
      subtext: d.city ?? d.location ?? "",
      url: d.displayUrl,
      media: d.logoUrl ? [d.logoUrl] : undefined,
      extras: {
        category: d.category ?? d.description ?? "",
        kategorieType: (d.type ?? d.category ?? "").toString().toUpperCase(),
        status: "active",
      },
    }),
  },
  partnerSpotlight: {
    mode: "replace",
    build: (d) => ({
      text: d.name.toUpperCase(),
      subtext: d.city ?? d.location ?? d.tagline ?? "",
      media: d.logoUrl ? [d.logoUrl] : undefined,
      extras: {
        category: (d.type ?? d.category ?? "").toString().toUpperCase(),
      },
    }),
  },
  partnerComingSoon: {
    mode: "replace",
    build: (d) => ({
      text: d.name.toUpperCase(),
      subtext: d.city ?? d.location ?? "Demnächst auf Allyver.",
      number: d.type ?? d.category ?? "",
      media: d.logoUrl ? [d.logoUrl] : undefined,
      extras: {
        category: (d.category ?? d.type ?? "").toString().toUpperCase(),
      },
    }),
  },
  partnerReveal: {
    mode: "replace",
    build: (d) => ({
      text: d.name.toUpperCase(),
      subtext: "Jetzt live auf Allyver",
      number: d.type ?? d.category ?? "",
      media: d.logoUrl ? [d.logoUrl] : undefined,
      extras: {
        category: (d.category ?? d.type ?? "").toString().toUpperCase(),
        stampLabel: "Jetzt Live",
        hintBefore: "Wer wird der nächste Allyver-Partner?",
        hintAfter: "Frisch im Marktplatz",
      },
    }),
  },
  partnerCategoryLineup: {
    mode: "append",
    build: (d, currentItems) => {
      const status = "active";
      const line = `${d.name}|${d.city ?? d.location ?? ""}|${status}`;
      // Replace if a row for this partner already exists, otherwise append (max 5 Zeilen).
      const idx = currentItems.findIndex(
        (l) => l.toLowerCase().startsWith(`${d.name.toLowerCase()}|`),
      );
      const items =
        idx >= 0
          ? currentItems.map((l, i) => (i === idx ? line : l))
          : [...currentItems, line].slice(0, 5);
      return {
        items,
        extras: { category: (d.category ?? d.type ?? "").toString().toUpperCase() },
      };
    },
    placeholder: "https://allyver.com/<händler-slug> → wird zur Liste hinzugefügt",
  },
  partnerStatusBoard: {
    mode: "append",
    build: (d, currentItems) => {
      const type = (d.type ?? d.category ?? "Dienstleister").toString();
      const line = `${d.name}|${type}|${d.city ?? d.location ?? ""}|active`;
      const idx = currentItems.findIndex(
        (l) => l.toLowerCase().startsWith(`${d.name.toLowerCase()}|`),
      );
      const items =
        idx >= 0
          ? currentItems.map((l, i) => (i === idx ? line : l))
          : [...currentItems, line].slice(0, 8);
      return { items };
    },
    placeholder: "https://allyver.com/<händler-slug> → wird zur Status-Tafel hinzugefügt",
  },
  partnerServiceTeaser: {
    mode: "replace",
    build: (d) => {
      // Singular ODER Plural automatisch aus dem dominanten Item-Kind +
      // der Anzahl ableiten. Bei genau 1 Angebot zeigen wir die
      // Singularform („1 Dienstleistung" statt „1 Dienstleistungen").
      const counts: Record<string, number> = {};
      for (const it of d.items) counts[it.kind] = (counts[it.kind] ?? 0) + 1;
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
      const formsByKind: Record<string, { sing: string; plur: string }> = {
        Produkt: { sing: "Produkt", plur: "Produkte" },
        Dienstleistung: { sing: "Dienstleistung", plur: "Dienstleistungen" },
        Inserat: { sing: "Inserat", plur: "Inserate" },
        Event: { sing: "Event", plur: "Events" },
        Speisekarte: { sing: "Gericht", plur: "Gerichte" },
        Unterkunft: { sing: "Unterkunft", plur: "Unterkünfte" },
        Transport: { sing: "Transfer", plur: "Transfers" },
        Sonstiges: { sing: "Angebot", plur: "Angebote" },
      };
      const forms = (top && formsByKind[top]) ?? { sing: "Angebot", plur: "Angebote" };
      const n = d.items.length;
      const unit = n === 1 ? forms.sing : forms.plur;
      return {
        text: d.name.toUpperCase(),
        subtext: d.city ?? d.location ?? "",
        number: String(n),
        unit,
      };
    },
  },
  partnerServiceList: {
    mode: "replace",
    build: (d) => {
      // Pipe-Format `Titel|Beschreibung|Preis|BildURL` funktioniert in BEIDEN
      // Layout-Modi (`compact` ignoriert die Zusatzfelder, `accordion` zeigt
      // sie als Detail-Karte). Trailing leere Felder werden weggetrimmt.
      const items = d.items.slice(0, 6).map((it) => {
        const parts: string[] = [
          it.name,
          "", // description (Allyver liefert leider keine Item-Beschreibung)
          it.priceLabel ?? "",
          it.imageUrl ?? "",
        ];
        while (parts.length > 1 && !parts[parts.length - 1]) parts.pop();
        return parts.join("|");
      });
      return {
        text: d.name,
        subtext: d.city ?? d.location ?? "",
        url: d.displayUrl,
        media: d.logoUrl ? [d.logoUrl] : undefined,
        items,
        extras: {
          kategorieType: (d.type ?? d.category ?? "").toString().toUpperCase(),
        },
      };
    },
  },
  partnerOfferReveal: {
    mode: "replace",
    build: (d) => {
      // Bis zu 4 Angebote im Pipe-Format `Titel|Beschreibung|Preis|BildURL`.
      // Bild + Preis werden auf den Karten gezeigt → wir behalten alle Felder.
      const items = d.items.slice(0, 4).map((it) => {
        const parts: string[] = [it.name, "", it.priceLabel ?? "", it.imageUrl ?? ""];
        while (parts.length > 1 && !parts[parts.length - 1]) parts.pop();
        return parts.join("|");
      });
      return {
        text: d.name.toUpperCase(),
        subtext: d.city ?? d.location ?? "",
        url: d.displayUrl,
        media: d.logoUrl ? [d.logoUrl] : undefined,
        items,
        extras: {
          kategorieType: (d.type ?? d.category ?? "ANGEBOTE").toString().toUpperCase(),
        },
      };
    },
  },
  partnerHeroReveal: {
    mode: "replace",
    build: (d) => {
      // Chips zeigen nur Titel + Preis → BildURL bleibt leer, Format aber
      // identisch (`Titel|Beschreibung|Preis|BildURL`) für Austauschbarkeit.
      const items = d.items.slice(0, 4).map((it) => {
        const parts: string[] = [it.name, "", it.priceLabel ?? ""];
        while (parts.length > 1 && !parts[parts.length - 1]) parts.pop();
        return parts.join("|");
      });
      return {
        text: d.name.toUpperCase(),
        subtext: d.city ?? d.location ?? "Jetzt live auf Allyver",
        url: d.displayUrl,
        media: d.logoUrl ? [d.logoUrl] : undefined,
        items,
        extras: {
          category: (d.type ?? d.category ?? "Partner").toString(),
        },
      };
    },
  },
};

const PARTNER_SCENES_WITH_LOADER = new Set(Object.keys(PARTNER_SCENE_MAPPINGS));

const PartnerLinkLoader: React.FC<{
  scene: ReelScene;
  onApply: (patch: PartnerLoaderPatch) => void;
}> = ({ scene, onApply }) => {
  const mapping = PARTNER_SCENE_MAPPINGS[scene.sceneId];
  const initialUrl = String(scene.inputs.url ?? scene.inputs.extras?.partnerUrl ?? "").trim();
  const [url, setUrl] = React.useState<string>(initialUrl);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [hint, setHint] = React.useState<string | null>(null);

  React.useEffect(() => {
    setUrl(String(scene.inputs.url ?? scene.inputs.extras?.partnerUrl ?? "").trim());
    setErr(null);
    setHint(null);
  }, [scene.uid]);

  if (!mapping) return null;

  const load = async (): Promise<void> => {
    const trimmed = url.trim();
    if (!trimmed) {
      setErr("Bitte URL eingeben.");
      return;
    }
    setLoading(true);
    setErr(null);
    setHint(null);
    try {
      const data = await api.partnerExtract(trimmed, { mirror: true });
      const currentItems = Array.isArray(scene.inputs.items) ? scene.inputs.items : [];
      const patch = mapping.build(data, currentItems);
      onApply(patch);
      const itemsInfo =
        mapping.mode === "append" && patch.items
          ? ` · jetzt ${patch.items.length} Einträge`
          : data.items.length
            ? ` · ${data.items.length} Produkte/Services geladen`
            : "";
      setHint(`Geladen: ${data.name}${data.city ? " · " + data.city : ""}${itemsInfo}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="form-row"
      style={{
        background: "rgba(34, 211, 197, 0.07)",
        border: "1px solid rgba(34, 211, 197, 0.3)",
        borderRadius: 10,
        padding: 10,
        gap: 6,
      }}
    >
      <span style={{ fontWeight: 600 }}>
        🔗 Aus Allyver-Händler-Link laden
        <small style={{ display: "block", fontWeight: 400, opacity: 0.7, marginTop: 2 }}>
          {mapping.mode === "append"
            ? "Der Händler wird automatisch der Liste hinzugefügt (oder aktualisiert, falls schon drin). Logo wird lokal gespiegelt."
            : "Name, Stadt, Branche, Kategorie & Logo werden automatisch befüllt."}
        </small>
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="url"
          value={url}
          placeholder={mapping.placeholder ?? "https://allyver.com/jusatech"}
          onChange={(e) => setUrl(e.target.value)}
          disabled={loading}
          style={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void load();
            }
          }}
        />
        <button
          type="button"
          className="btn primary"
          onClick={() => void load()}
          disabled={loading || !url.trim()}
        >
          {loading ? "…" : mapping.mode === "append" ? "Hinzufügen" : "Laden"}
        </button>
      </div>
      {err ? (
        <small style={{ color: "#f87171" }}>{err}</small>
      ) : hint ? (
        <small style={{ color: "#34d399" }}>{hint}</small>
      ) : null}
    </div>
  );
};

function readField(inputs: SceneInputs, key: string): unknown {
  if (KNOWN_KEYS.has(key)) {
    return (inputs as unknown as Record<string, unknown>)[key];
  }
  return inputs.extras?.[key];
}

/**
 * Editor for `list` fields. Stores the raw textarea string in local state so
 * trailing newlines / empty in-progress lines don't get nuked the moment the
 * parent re-renders — that was the bug behind "Enter geht nicht in die
 * nächste Zeile". Items are still derived from the buffer (split on `\n`,
 * filtered) and pushed up via `onChange`. Shift+Enter inserts U+2028 inside
 * an item so paragraphs render with a line break without creating a new item.
 */
const ListFieldEditor: React.FC<{
  id: string;
  label: string;
  hint?: string;
  value: string[];
  onChange: (v: unknown) => void;
}> = ({ id, label, hint, value, onChange }) => {
  const externalText = value.join("\n");
  const [raw, setRaw] = React.useState<string>(externalText);
  // Track the last value we PUSHED upward so we can distinguish our own
  // emits from external mutations (e.g. AI-generated items, undo/redo).
  const lastEmitted = React.useRef<string>(externalText);

  React.useEffect(() => {
    if (externalText !== lastEmitted.current) {
      lastEmitted.current = externalText;
      setRaw(externalText);
    }
  }, [externalText]);

  const commit = (next: string): void => {
    setRaw(next);
    const items = next.split(/\r?\n/).filter((x) => x.trim().length > 0);
    const joined = items.join("\n");
    lastEmitted.current = joined;
    onChange(items);
  };

  const lineCount = raw.split(/\r?\n/).length;

  return (
    <label className="form-row" htmlFor={id}>
      <span>
        {label} <small>(eine Zeile pro Item · Shift+Enter für Absatz)</small>
      </span>
      <textarea
        id={id}
        rows={Math.max(4, lineCount + 1)}
        value={raw}
        placeholder={hint}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            const ta = e.currentTarget;
            const start = ta.selectionStart ?? ta.value.length;
            const end = ta.selectionEnd ?? start;
            const next = ta.value.slice(0, start) + "\u2028" + ta.value.slice(end);
            commit(next);
            requestAnimationFrame(() => {
              ta.selectionStart = ta.selectionEnd = start + 1;
            });
          }
        }}
        onChange={(e) => commit(e.target.value)}
      />
    </label>
  );
};

const FieldEditor: React.FC<{
  field: FieldDefinition;
  value: unknown;
  variants: string[];
  onChange: (v: unknown) => void;
}> = ({ field, value, variants, onChange }) => {
  const id = `f-${field.key}`;
  if (field.kind === "longtext") {
    return (
      <label className="form-row" htmlFor={id}>
        <span>{field.label}</span>
        <textarea
          id={id}
          rows={3}
          value={(value as string) ?? ""}
          placeholder={field.hint}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }
  if (field.kind === "list") {
    return (
      <ListFieldEditor
        id={id}
        label={field.label}
        hint={field.hint}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
      />
    );
  }
  if (field.kind === "media") {
    // The `media` slot in SceneInputs is typed as `string[]` because every
    // mockup scene reads `inputs.media[0]` (with optional further slots for
    // carousels). Single-mode MediaPicker yields a single string, so we
    // marshal between the two shapes here:
    //   - for display: prefer the array-form's first item, fall back to a
    //     bare string the editor may have written before this fix
    //   - for write:  if the field is the canonical `inputs.media` slot, we
    //     wrap into a single-element array so the scene reads it correctly.
    //     For ad-hoc media fields (e.g. `extras.screenshot`) we keep the
    //     plain-string shape the scene likely expects.
    const fromArray = Array.isArray(value) ? (value as string[])[0] : undefined;
    const url = fromArray ?? (typeof value === "string" ? value : undefined);
    const isCanonicalMediaSlot = field.key === "media";
    return (
      <div className="form-row">
        <MediaPicker
          mode="single"
          label={field.label}
          hint={field.hint}
          value={url}
          accept="any"
          onChange={(next) => {
            if (isCanonicalMediaSlot) {
              onChange(next ? [next] : []);
            } else {
              onChange(next ?? "");
            }
          }}
        />
      </div>
    );
  }
  if (field.kind === "mediaList") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div className="form-row">
        <MediaPicker
          mode="list"
          label={field.label}
          hint={field.hint}
          value={arr}
          count="auto"
          accept="any"
          onChange={(next) => onChange(next)}
        />
      </div>
    );
  }
  if (field.kind === "number") {
    return (
      <label className="form-row" htmlFor={id}>
        <span>{field.label}</span>
        <input
          id={id}
          type="number"
          value={(value as number | string) ?? ""}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        />
      </label>
    );
  }
  if (field.kind === "variant") {
    const opts = field.options ?? variants;
    return (
      <label className="form-row" htmlFor={id}>
        <span>{field.label}</span>
        <select id={id} value={(value as string) ?? opts[0]} onChange={(e) => onChange(e.target.value)}>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field.kind === "boolean") {
    const fallback = field.default === undefined ? true : Boolean(field.default);
    const on = value === undefined ? fallback : value === true || value === "true" || value === 1;
    return (
      <label className="form-row toggle-row" htmlFor={id}>
        <span>{field.label}</span>
        <button
          id={id}
          type="button"
          className={`toggle ${on ? "on" : "off"}`}
          onClick={() => onChange(!on)}
          aria-pressed={on}
          title={field.hint}
        >
          <span className="dot" />
          <span className="lbl">{on ? "An" : "Aus"}</span>
        </button>
      </label>
    );
  }
  if (field.kind === "accent") {
    return (
      <label className="form-row" htmlFor={id}>
        <span>{field.label}</span>
        <select id={id} value={(value as string) ?? "primary"} onChange={(e) => onChange(e.target.value)}>
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="accent">Accent</option>
        </select>
      </label>
    );
  }
  return (
    <label className="form-row" htmlFor={id}>
      <span>{field.label}</span>
      <input
        id={id}
        type="text"
        value={(value as string) ?? ""}
        placeholder={field.hint}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
};

/* =============================================================
 *  AllyverSlotsEditor — per-Slide Konfiguration der ALLYVER-Blöcke
 *  in der `colorBlockReveal`-Szene. Ein Eintrag pro Frage (= ein
 *  ALLYVER-Block dazwischen). Jeder Slot wählt Modus, optional
 *  Bild + Name + Subtitle.
 * ============================================================= */
type AllyverMode = "text" | "logo" | "card" | "image" | "editorial";
type AllyverSlot = {
  mode: AllyverMode;
  media?: string;
  name?: string;
  subtitle?: string;
};

const ALLYVER_MODE_LABELS: Record<AllyverMode, { label: string; desc: string; emoji: string }> = {
  text: { label: 'Text „ALLYVER"', desc: "Großes Wordmark in Akzentfarbe", emoji: "🅰" },
  logo: { label: "Allyver-Logo", desc: "PNG-Logo zentriert", emoji: "🖼" },
  card: { label: "Card (Bild + Name)", desc: "Quadratisches Bild + Name + Tagline", emoji: "🎴" },
  image: { label: "Full-Bleed Bild", desc: "Bild komplett, Name als Overlay unten", emoji: "🌃" },
  editorial: { label: "Editorial / Magazine", desc: "Großes Bild oben + Name unten + Tag", emoji: "📰" },
};

function readAllyverSlots(raw: unknown): AllyverSlot[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry): AllyverSlot => {
    if (!entry || typeof entry !== "object") return { mode: "text" };
    const obj = entry as Record<string, unknown>;
    const m = typeof obj.mode === "string" ? obj.mode : "text";
    const mode: AllyverMode =
      m === "logo" || m === "card" || m === "image" || m === "editorial" ? m : "text";
    return {
      mode,
      media: typeof obj.media === "string" ? obj.media : undefined,
      name: typeof obj.name === "string" ? obj.name : undefined,
      subtitle: typeof obj.subtitle === "string" ? obj.subtitle : undefined,
    };
  });
}

function effectiveQuestions(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((q) => (typeof q === "string" ? q.trim() : ""))
    .filter((q) => {
      if (!q) return false;
      const u = q.toUpperCase();
      return u !== "ALLYVER" && u !== "ALLYVER." && q !== "@logo" && u !== "@LOGO";
    })
    .slice(0, 8); // Scene cap: maximal 8 Fragen → 16 Blöcke
}

const AllyverSlotsEditor: React.FC<{
  scene: ReelScene;
  onPatchExtras: (patch: Record<string, unknown>) => void;
}> = ({ scene, onPatchExtras }) => {
  const questions = effectiveQuestions(scene.inputs.items);
  const globalFallback: AllyverMode = scene.inputs.extras?.allyverAsLogo === true ? "logo" : "text";
  const slots = readAllyverSlots(scene.inputs.extras?.allyverSlots);

  // Padded view: für jede Frage einen Eintrag (Fallback-Modus, wenn nichts gesetzt).
  const padded: AllyverSlot[] = questions.map((_, i) => slots[i] ?? { mode: globalFallback });

  const writeSlots = (next: AllyverSlot[]): void => {
    // Trim trailing default entries, damit der gespeicherte Array nicht
    // unnötig wächst — wenn ein Slot leer (Fallback) ist, lassen wir ihn
    // implizit. Speichern alle bis zum letzten "anders konfigurierten" Slot.
    let lastNonDefault = -1;
    next.forEach((s, i) => {
      const isDefault =
        s.mode === globalFallback && !s.media && !s.name && !s.subtitle;
      if (!isDefault) lastNonDefault = i;
    });
    const trimmed = lastNonDefault >= 0 ? next.slice(0, lastNonDefault + 1) : [];
    onPatchExtras({ allyverSlots: trimmed });
  };

  const updateSlot = (idx: number, patch: Partial<AllyverSlot>): void => {
    const next = padded.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    writeSlots(next);
  };

  const resetSlot = (idx: number): void => {
    const next = padded.map((s, i) => (i === idx ? { mode: globalFallback } : s));
    writeSlots(next);
  };

  const setAllToMode = (mode: AllyverMode): void => {
    const next = padded.map(() => ({ mode } as AllyverSlot));
    writeSlots(next);
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <details className="inspector-section" open>
      <summary>
        ✨ ALLYVER-Slides ({questions.length})
        <small style={{ marginLeft: 6, opacity: 0.6 }}>
          · individuell pro Wechsel
        </small>
      </summary>

      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          padding: "8px 0 12px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 11, opacity: 0.6, alignSelf: "center", marginRight: 4 }}>
          Alle setzen auf:
        </span>
        {(Object.keys(ALLYVER_MODE_LABELS) as AllyverMode[]).map((m) => (
          <button
            key={m}
            type="button"
            className="btn ghost sm"
            onClick={() => setAllToMode(m)}
            title={ALLYVER_MODE_LABELS[m].desc}
            style={{ fontSize: 11, padding: "4px 10px" }}
          >
            {ALLYVER_MODE_LABELS[m].emoji} {ALLYVER_MODE_LABELS[m].label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {questions.map((q, i) => {
          const slot = padded[i]!;
          const needsMedia =
            slot.mode === "card" || slot.mode === "image" || slot.mode === "editorial";
          const isCustom =
            slot.mode !== globalFallback || !!slot.media || !!slot.name || !!slot.subtitle;
          return (
            <div
              key={i}
              style={{
                padding: 12,
                background: isCustom
                  ? "rgba(34, 211, 197, 0.05)"
                  : "rgba(255,255,255,0.025)",
                border: `1px solid ${isCustom ? "rgba(34, 211, 197, 0.25)" : "rgba(255,255,255,0.08)"}`,
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}>
                  <span style={{ opacity: 0.5 }}>nach Frage {i + 1}:</span>{" "}
                  <span style={{ fontStyle: "italic" }}>„{q}"</span>
                </div>
                {isCustom ? (
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={() => resetSlot(i)}
                    title="Zurück zum globalen Default"
                    style={{ fontSize: 10, padding: "2px 8px", opacity: 0.7 }}
                  >
                    Reset
                  </button>
                ) : null}
              </div>

              <label className="form-row" style={{ marginBottom: needsMedia ? 10 : 0 }}>
                <span style={{ fontSize: 12 }}>Modus</span>
                <select
                  value={slot.mode}
                  onChange={(e) =>
                    updateSlot(i, { mode: e.target.value as AllyverMode })
                  }
                  style={{ fontSize: 12 }}
                >
                  {(Object.keys(ALLYVER_MODE_LABELS) as AllyverMode[]).map((m) => (
                    <option key={m} value={m}>
                      {ALLYVER_MODE_LABELS[m].emoji} {ALLYVER_MODE_LABELS[m].label}
                    </option>
                  ))}
                </select>
              </label>

              {needsMedia ? (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <MediaPicker
                      mode="single"
                      label="Händler-/Produktbild"
                      hint="Logo, Foto oder Produkt-Bild des Händlers (wird im gewählten Modus eingebunden)."
                      value={slot.media}
                      accept="image"
                      onChange={(next) => updateSlot(i, { media: next ?? "" })}
                    />
                  </div>
                  <label className="form-row" style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 12 }}>Name / Titel</span>
                    <input
                      type="text"
                      value={slot.name ?? ""}
                      placeholder={`z. B. „Pizzeria Rossi"`}
                      onChange={(e) => updateSlot(i, { name: e.target.value })}
                      style={{ fontSize: 12 }}
                    />
                  </label>
                  <label className="form-row">
                    <span style={{ fontSize: 12 }}>Tagline / Kategorie</span>
                    <input
                      type="text"
                      value={slot.subtitle ?? ""}
                      placeholder={`z. B. „Restaurant", „Taxi-Service"`}
                      onChange={(e) => updateSlot(i, { subtitle: e.target.value })}
                      style={{ fontSize: 12 }}
                    />
                  </label>
                </>
              ) : null}

              <div
                style={{
                  marginTop: 8,
                  fontSize: 10,
                  opacity: 0.55,
                  lineHeight: 1.4,
                }}
              >
                {ALLYVER_MODE_LABELS[slot.mode].desc}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, opacity: 0.55, lineHeight: 1.5 }}>
        Globaler Fallback ist „{ALLYVER_MODE_LABELS[globalFallback].label}" (Toggle „ALLYVER-Slide als
        Logo statt Text" weiter oben). Slots ohne explizite Konfiguration zeigen den Fallback.
      </div>
    </details>
  );
};
